import { useEffect } from "react";

/**
 * QA 2026-10-28 (Android, ~375px): "switching between tabs doesn't keep the
 * active tab in view". A tab strip wider than the screen scrolls sideways; a
 * tab that is a page of its own remounts the strip at scroll 0 on every
 * switch, so the tab you just picked slid off the right edge.
 *
 * One rule for every strip in the app, from the shell, instead of a fix per
 * component: whenever the active tab of a horizontally scrolling strip
 * changes, scroll THAT STRIP sideways until the tab (and a little of its
 * neighbour) is in view. It never scrolls the page vertically, and it leaves a
 * strip alone while its active tab is unchanged, so a manager scrolling the
 * strip by hand to look at the other tabs is not pulled back.
 */

const ACTIVE_ITEM = [
  '[role="tab"][aria-selected="true"]',
  '[role="tab"][data-state="active"]',
  '[aria-current="page"]',
  '[aria-current="true"]',
].join(",");

/** Only tabs and tab-like navigation: never a link that happens to sit in a wide table. */
const STRIP = 'nav, [role="tablist"], [data-tab-strip]';

function scrollsSideways(node: HTMLElement): boolean {
  if (node.scrollWidth <= node.clientWidth + 1) return false;
  const { overflowX } = getComputedStyle(node);
  return overflowX === "auto" || overflowX === "scroll";
}

/** The nearest ancestor of `item` that scrolls sideways and is part of its strip. */
function stripScrollerOf(item: HTMLElement): HTMLElement | null {
  const strip = item.closest<HTMLElement>(STRIP);
  if (!strip) return null;
  // The scroller is the strip, something inside it, or a wrapper just around it.
  let node: HTMLElement | null = item.parentElement;
  let hops = 0;
  while (node && node !== document.body && hops < 8) {
    if (scrollsSideways(node)) return node;
    node = node.parentElement;
    hops += 1;
  }
  return null;
}

/**
 * Scroll `strip` sideways so `item` is fully in view with a margin, so the
 * next tab peeks out and says there is more. Exported for its test.
 */
export function revealInStrip(strip: HTMLElement, item: HTMLElement, smooth: boolean): void {
  const stripBox = strip.getBoundingClientRect();
  const itemBox = item.getBoundingClientRect();
  const start = itemBox.left - stripBox.left + strip.scrollLeft;
  const end = start + itemBox.width;
  const margin = Math.min(48, strip.clientWidth / 4);
  let target: number | null = null;
  if (start - margin < strip.scrollLeft) target = start - margin;
  else if (end + margin > strip.scrollLeft + strip.clientWidth) target = end + margin - strip.clientWidth;
  if (target === null) return;
  const left = Math.max(0, Math.min(target, strip.scrollWidth - strip.clientWidth));
  if (typeof strip.scrollTo === "function") {
    strip.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
  } else {
    strip.scrollLeft = left;
  }
}

/** Mount once, at the app's root. */
export function useKeepActiveTabsInView(): void {
  useEffect(() => {
    if (typeof MutationObserver === "undefined" || typeof requestAnimationFrame === "undefined") return;
    // The tab each strip last revealed. A strip that remounted is a new element: it has none.
    let revealed = new WeakMap<HTMLElement, HTMLElement>();
    let frame = 0;

    const check = () => {
      frame = 0;
      for (const item of document.querySelectorAll<HTMLElement>(ACTIVE_ITEM)) {
        const strip = stripScrollerOf(item);
        if (!strip || revealed.get(strip) === item) continue;
        // A strip seen for the first time (a page just opened) jumps; a switch in place glides.
        const firstLook = !revealed.has(strip);
        revealed.set(strip, item);
        revealInStrip(strip, item, !firstLook);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    // A narrower window can push the active tab out of a strip that had room for it.
    const onResize = () => {
      revealed = new WeakMap();
      schedule();
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-current", "aria-selected", "data-state"],
    });
    window.addEventListener("resize", onResize);
    schedule();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
}

/** `useKeepActiveTabsInView` as an element, for the root's list of app-wide behaviours. */
export function KeepActiveTabsInView(): null {
  useKeepActiveTabsInView();
  return null;
}
